const state = {
  summaries: {}, // Keyed by customerId: { [customerId]: summary }
  transactions: {}, // Keyed by customerId: { [customerId]: { transactions, programId } }
  availableRewards: {}, // Keyed by customerId: { [customerId]: { rewards, programId } }
  loading: false,
  error: null,
  lastFetch: {} // Track cache timestamps: { [customerId]: { summary: timestamp, transactions: timestamp, rewards: timestamp } }
}

const mutations = {
  SET_SUMMARY (state, { customerId, summary, timestamp }) {
    state.summaries = {
      ...state.summaries,
      [customerId]: summary
    }
    if (!state.lastFetch[customerId]) {
      state.lastFetch[customerId] = {}
    }
    state.lastFetch[customerId].summary = timestamp
  },
  SET_TRANSACTIONS (state, { customerId, transactions, programId, timestamp }) {
    state.transactions = {
      ...state.transactions,
      [customerId]: { transactions, programId }
    }
    if (!state.lastFetch[customerId]) {
      state.lastFetch[customerId] = {}
    }
    state.lastFetch[customerId].transactions = timestamp
  },
  ADD_TRANSACTION (state, { customerId, transaction }) {
    const existing = state.transactions[customerId]
    if (existing) {
      existing.transactions.unshift(transaction) // Add to beginning
    }
  },
  SET_AVAILABLE_REWARDS (state, { customerId, rewards, programId, timestamp }) {
    state.availableRewards = {
      ...state.availableRewards,
      [customerId]: { rewards, programId }
    }
    if (!state.lastFetch[customerId]) {
      state.lastFetch[customerId] = {}
    }
    state.lastFetch[customerId].rewards = timestamp
  },
  UPDATE_SUMMARY_POINTS (state, { customerId, programId, pointsDelta }) {
    const summary = state.summaries[customerId]
    if (summary && summary.programs) {
      const program = summary.programs.find(p => p.program_id === programId)
      if (program) {
        program.current_points = Math.max(0, program.current_points + pointsDelta)
      }
    }
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ERROR (state, error) {
    state.error = error
  }
}

const actions = {
  async fetchSummary ({ commit, state }, { customerId, programId = null }) {
    // Check cache (30 seconds)
    const cacheTimeout = 30 * 1000
    const lastFetch = state.lastFetch[customerId]?.summary
    if (lastFetch && (Date.now() - lastFetch < cacheTimeout)) {
      return state.summaries[customerId]
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const summary = await this._vm.$loyaltyService.getCustomerSummary(customerId, programId)
      commit('SET_SUMMARY', { customerId, summary, timestamp: Date.now() })
      return summary
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTransactions ({ commit, state }, { customerId, programId = null, limit = 50 }) {
    // Check cache (1 minute)
    const cacheTimeout = 60 * 1000
    const lastFetch = state.lastFetch[customerId]?.transactions
    if (lastFetch && (Date.now() - lastFetch < cacheTimeout)) {
      const existing = state.transactions[customerId]
      if (existing && existing.programId === programId) {
        return existing.transactions
      }
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this._vm.$loyaltyService.getCustomerTransactions(customerId, { program_id: programId, limit })
      const transactions = response.transactions || []
      commit('SET_TRANSACTIONS', { customerId, transactions, programId, timestamp: Date.now() })
      return transactions
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAvailableRewards ({ commit, state }, { customerId, programId }) {
    // Check cache (2 minutes)
    const cacheTimeout = 2 * 60 * 1000
    const lastFetch = state.lastFetch[customerId]?.rewards
    if (lastFetch && (Date.now() - lastFetch < cacheTimeout)) {
      const existing = state.availableRewards[customerId]
      if (existing && existing.programId === programId) {
        return existing.rewards
      }
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this._vm.$loyaltyService.getCustomerAvailableRewards(customerId, programId)
      const rewards = response.rewards || []
      commit('SET_AVAILABLE_REWARDS', { customerId, rewards, programId, timestamp: Date.now() })
      return rewards
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async adjustPoints ({ commit }, { customerId, programId, points, description }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const result = await this._vm.$loyaltyService.adjustCustomerPoints(customerId, {
        program_id: programId,
        points,
        description
      })
      // Update summary points
      commit('UPDATE_SUMMARY_POINTS', { customerId, programId, pointsDelta: points })
      // Add transaction
      commit('ADD_TRANSACTION', { customerId, transaction: result.transaction })
      return result
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async redeemReward ({ commit }, { customerId, rewardId, referenceType = null, referenceId = null }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const redemption = await this._vm.$loyaltyService.redeemReward(customerId, {
        reward_id: rewardId,
        reference_type: referenceType,
        reference_id: referenceId
      })
      // Update summary points (deduct)
      const pointsUsed = redemption.points_used || 0
      commit('UPDATE_SUMMARY_POINTS', { customerId, programId: redemption.loyalty_program_id, pointsDelta: -pointsUsed })
      // Add transaction
      commit('ADD_TRANSACTION', { customerId, transaction: redemption.transaction })
      return redemption
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  customerSummary: state => customerId => {
    return state.summaries[customerId] || null
  },
  customerPoints: (state, getters) => (customerId, programId = null) => {
    const summary = getters.customerSummary(customerId)
    if (!summary) return 0

    if (programId) {
      const program = summary.programs?.find(p => p.program_id === programId)
      return program?.current_points || 0
    }

    return summary.total_points || 0
  },
  customerTier: (state, getters) => (customerId, programId) => {
    const summary = getters.customerSummary(customerId)
    if (!summary || !programId) return null

    const program = summary.programs?.find(p => p.program_id === programId)
    return program
      ? {
          id: program.tier_id,
          name: program.tier_name
        }
      : null
  },
  recentTransactions: (state, getters) => (customerId, limit = 10) => {
    const transactions = state.transactions[customerId]?.transactions || []
    return transactions.slice(0, limit)
  },
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
